/**
 * Ripia JWT署名・検証システム
 * エンタープライズレベルRS256/ES256実装
 *
 * @description 非対称暗号化JWT署名・検証・ペイロード管理
 * @security RSA-PKCS1-v1_5/ECDSA-P256署名検証
 * @integration jwtConfig.ts・crypto.ts統合
 */

import CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';
import {
  getJWTConfig,
  validateJWTConfig,
  generateJWTHeader,
  type JWTConfig,
  type JWTAlgorithm,
} from './jwtConfig';
import { generateDataHash } from './crypto';
import logger from './logger';

// JWT署名エラー
export class JWTSignerError extends Error {
  constructor(
    message: string,
    public code:
      | 'INVALID_PAYLOAD'
      | 'SIGNATURE_FAILED'
      | 'VERIFICATION_FAILED'
      | 'EXPIRED_TOKEN'
      | 'MALFORMED_TOKEN'
  ) {
    super(message);
    this.name = 'JWTSignerError';
  }
}

// Ripia拡張JWTペイロード
export interface RipiaJWTPayload {
  // 標準Claims
  sub: string; // Subject (User ID)
  iss: string; // Issuer
  aud: string[]; // Audience
  iat: number; // Issued At
  exp: number; // Expiration Time
  nbf?: number; // Not Before
  jti: string; // JWT ID（一意識別・リボーク用）

  // 既存Claims（authService互換性）
  email: string; // ユーザーメール
  role: string; // 基本権限
  storeId?: string; // 店舗ID
  permissions?: string[]; // 詳細権限

  // マルチテナント拡張Claims
  tenant_id?: string; // テナントID
  tenant_role?: string; // テナント内役割
  plan_type?: string; // プラン種別
  tenant_permissions?: string[]; // テナント権限

  // セキュリティ強化Claims
  device_fp?: string; // デバイスフィンガープリント
  session_id?: string; // セッション管理
  refresh_jti?: string; // リフレッシュトークンJTI
}

// JWT検証結果
export interface JWTVerificationResult {
  valid: boolean;
  payload?: RipiaJWTPayload;
  header?: object;
  error?: string;
  expired?: boolean;
  malformed?: boolean;
}

// JWTトークンペア
export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: 'Bearer';
  jti: string;
  refreshJti: string;
}

/**
 * JWT署名（開発環境モック実装）
 * 本番環境ではサーバーサイドで実際のRS256/ES256署名を実行
 * @param payload ペイロード
 * @param config JWT設定
 * @returns 署名されたJWT
 */
const signJWT = (payload: object, config: JWTConfig): string => {
  try {
    // JWTヘッダー生成
    const header = generateJWTHeader(config);

    // Base64Url エンコード
    const encodedHeader = base64UrlEncode(JSON.stringify(header));
    const encodedPayload = base64UrlEncode(JSON.stringify(payload));

    // 署名対象データ
    const signingInput = `${encodedHeader}.${encodedPayload}`;

    // 開発環境での署名モック実装
    let signature: string;

    if (config.algorithm === 'RS256') {
      // RS256シミュレーション（実際は秘密鍵でRSA署名）
      const mockSignature = CryptoJS.HmacSHA256(
        signingInput,
        config.privateKey
      );
      signature = base64UrlEncode(mockSignature.toString());
    } else if (config.algorithm === 'ES256') {
      // ES256シミュレーション（実際は秘密鍵でECDSA署名）
      const mockSignature = CryptoJS.HmacSHA256(
        signingInput,
        config.privateKey
      );
      signature = base64UrlEncode(mockSignature.toString());
    } else {
      throw new JWTSignerError(
        `サポートされていない署名アルゴリズム: ${config.algorithm}`,
        'SIGNATURE_FAILED'
      );
    }

    // JWT完成（header.payload.signature）
    const jwt = `${signingInput}.${signature}`;

    logger.debug(
      `JWT署名完了: ${config.algorithm} (JTI: ${(payload as RipiaJWTPayload).jti})`
    );
    return jwt;
  } catch (error) {
    logger.error('JWT署名エラー:', error);
    throw new JWTSignerError(
      `JWT署名に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'SIGNATURE_FAILED'
    );
  }
};

/**
 * JWT検証（開発環境モック実装）
 * 本番環境ではサーバーサイドで実際のRS256/ES256検証を実行
 * @param jwt JWT トークン
 * @param config JWT設定
 * @returns 検証結果
 */
const verifyJWT = (jwt: string, config: JWTConfig): JWTVerificationResult => {
  try {
    // JWT形式確認
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      return {
        valid: false,
        error: 'JWT形式が無効です',
        malformed: true,
      };
    }

    const [encodedHeader, encodedPayload, signature] = parts;

    // ヘッダー・ペイロード復号化
    const header = JSON.parse(base64UrlDecode(encodedHeader));
    const payload = JSON.parse(
      base64UrlDecode(encodedPayload)
    ) as RipiaJWTPayload;

    // アルゴリズム確認
    if (header.alg !== config.algorithm) {
      return {
        valid: false,
        error: `署名アルゴリズムが一致しません: expected ${config.algorithm}, got ${header.alg}`,
        header,
        payload,
      };
    }

    // 有効期限確認
    const currentTime = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < currentTime) {
      return {
        valid: false,
        error: 'JWT has expired',
        expired: true,
        header,
        payload,
      };
    }

    // Not Before確認
    if (payload.nbf && payload.nbf > currentTime) {
      return {
        valid: false,
        error: 'JWT not yet valid',
        header,
        payload,
      };
    }

    // Issuer/Audience確認
    if (payload.iss !== config.issuer) {
      return {
        valid: false,
        error: `Issuer不一致: expected ${config.issuer}, got ${payload.iss}`,
        header,
        payload,
      };
    }

    if (
      !payload.aud ||
      !config.audience.some((aud) => payload.aud.includes(aud))
    ) {
      return {
        valid: false,
        error: 'Audience不一致',
        header,
        payload,
      };
    }

    // 署名検証（開発環境モック）
    const signingInput = `${encodedHeader}.${encodedPayload}`;
    let expectedSignature: string;

    if (config.algorithm === 'RS256') {
      const mockSignature = CryptoJS.HmacSHA256(signingInput, config.publicKey);
      expectedSignature = base64UrlEncode(mockSignature.toString());
    } else if (config.algorithm === 'ES256') {
      const mockSignature = CryptoJS.HmacSHA256(signingInput, config.publicKey);
      expectedSignature = base64UrlEncode(mockSignature.toString());
    } else {
      return {
        valid: false,
        error: `サポートされていない検証アルゴリズム: ${config.algorithm}`,
        header,
        payload,
      };
    }

    // 署名一致確認
    if (signature !== expectedSignature) {
      return {
        valid: false,
        error: '署名検証に失敗しました',
        header,
        payload,
      };
    }

    logger.debug(`JWT検証成功: ${config.algorithm} (JTI: ${payload.jti})`);

    return {
      valid: true,
      payload,
      header,
    };
  } catch (error) {
    logger.error('JWT検証エラー:', error);
    return {
      valid: false,
      error: `JWT検証に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      malformed: true,
    };
  }
};

/**
 * Base64Url エンコード
 */
const base64UrlEncode = (data: string): string => {
  return btoa(data).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

/**
 * Base64Url デコード
 */
const base64UrlDecode = (data: string): string => {
  // パディング追加
  const padded = data + '='.repeat((4 - (data.length % 4)) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64);
};

/**
 * アクセストークン生成
 * @param userPayload ユーザー情報
 * @param algorithm 署名アルゴリズム
 * @returns アクセストークン
 */
export const generateAccessToken = (
  userPayload: Partial<RipiaJWTPayload>,
  algorithm: JWTAlgorithm = 'RS256'
): string => {
  try {
    const config = getJWTConfig(algorithm);
    validateJWTConfig(config);

    const now = Math.floor(Date.now() / 1000);
    const jti = uuidv4();

    const payload: RipiaJWTPayload = {
      // 標準Claims
      sub: userPayload.sub || '',
      iss: config.issuer,
      aud: config.audience,
      iat: now,
      exp: now + config.expiresIn.accessToken,
      jti,

      // ユーザーデータ
      email: userPayload.email || '',
      role: userPayload.role || '',
      storeId: userPayload.storeId,
      permissions: userPayload.permissions || [],

      // マルチテナント
      tenant_id: userPayload.tenant_id,
      tenant_role: userPayload.tenant_role,
      plan_type: userPayload.plan_type,
      tenant_permissions: userPayload.tenant_permissions,

      // セキュリティ
      device_fp: userPayload.device_fp,
      session_id: userPayload.session_id,
    };

    return signJWT(payload, config);
  } catch (error) {
    logger.error('アクセストークン生成エラー:', error);
    throw new JWTSignerError(
      `アクセストークン生成に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'SIGNATURE_FAILED'
    );
  }
};

/**
 * リフレッシュトークン生成
 * @param userPayload ユーザー情報
 * @param algorithm 署名アルゴリズム
 * @returns リフレッシュトークン
 */
export const generateRefreshToken = (
  userPayload: Partial<RipiaJWTPayload>,
  algorithm: JWTAlgorithm = 'RS256'
): string => {
  try {
    const config = getJWTConfig(algorithm);
    validateJWTConfig(config);

    const now = Math.floor(Date.now() / 1000);
    const refreshJti = uuidv4();

    const payload: RipiaJWTPayload = {
      // 標準Claims
      sub: userPayload.sub || '',
      iss: config.issuer,
      aud: config.audience,
      iat: now,
      exp: now + config.expiresIn.refreshToken,
      jti: refreshJti,

      // 最小限の情報（セキュリティ）
      email: userPayload.email || '',
      role: 'refresh',
      permissions: [],

      // リフレッシュ用識別
      session_id: userPayload.session_id,
      device_fp: userPayload.device_fp,
    };

    return signJWT(payload, config);
  } catch (error) {
    logger.error('リフレッシュトークン生成エラー:', error);
    throw new JWTSignerError(
      `リフレッシュトークン生成に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'SIGNATURE_FAILED'
    );
  }
};

/**
 * トークンペア生成
 * @param userPayload ユーザー情報
 * @param algorithm 署名アルゴリズム
 * @returns トークンペア
 */
export const generateTokenPair = (
  userPayload: Partial<RipiaJWTPayload>,
  algorithm: JWTAlgorithm = 'RS256'
): TokenPair => {
  try {
    const config = getJWTConfig(algorithm);
    const jti = uuidv4();
    const refreshJti = uuidv4();

    // セッションID統一
    const sessionId = userPayload.session_id || uuidv4();
    const enhancedPayload = { ...userPayload, session_id: sessionId };

    const accessToken = generateAccessToken(
      { ...enhancedPayload, jti },
      algorithm
    );
    const refreshToken = generateRefreshToken(
      { ...enhancedPayload, jti: refreshJti },
      algorithm
    );

    return {
      accessToken,
      refreshToken,
      expiresIn: config.expiresIn.accessToken,
      tokenType: 'Bearer',
      jti,
      refreshJti,
    };
  } catch (error) {
    logger.error('トークンペア生成エラー:', error);
    throw new JWTSignerError(
      `トークンペア生成に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      'SIGNATURE_FAILED'
    );
  }
};

/**
 * JWT検証（公開API）
 * @param jwt JWT トークン
 * @param algorithm 検証アルゴリズム
 * @returns 検証結果
 */
export const verifyToken = (
  jwt: string,
  algorithm: JWTAlgorithm = 'RS256'
): JWTVerificationResult => {
  try {
    const config = getJWTConfig(algorithm);
    return verifyJWT(jwt, config);
  } catch (error) {
    logger.error('JWT検証エラー:', error);
    return {
      valid: false,
      error: `JWT検証に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      malformed: true,
    };
  }
};

/**
 * JWT情報抽出（検証なし）
 * @param jwt JWT トークン
 * @returns ペイロード情報
 */
export const extractJWTPayload = (jwt: string): RipiaJWTPayload | null => {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(base64UrlDecode(parts[1])) as RipiaJWTPayload;
    return payload;
  } catch (error) {
    logger.error('JWTペイロード抽出エラー:', error);
    return null;
  }
};

/**
 * JWT有効期限チェック
 * @param jwt JWT トークン
 * @returns 有効性情報
 */
export const checkTokenExpiry = (
  jwt: string
): { valid: boolean; expiresIn: number; expired: boolean } => {
  try {
    const payload = extractJWTPayload(jwt);
    if (!payload || !payload.exp) {
      return { valid: false, expiresIn: 0, expired: true };
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const expiresIn = payload.exp - currentTime;
    const expired = expiresIn <= 0;

    return {
      valid: !expired,
      expiresIn: Math.max(0, expiresIn),
      expired,
    };
  } catch (error) {
    logger.error('JWT有効期限チェックエラー:', error);
    return { valid: false, expiresIn: 0, expired: true };
  }
};

/**
 * デバイスフィンガープリント生成
 * @returns デバイス固有識別子
 */
export const generateDeviceFingerprint = (): string => {
  try {
    // ブラウザ固有情報収集
    const components = [
      navigator.userAgent,
      navigator.language,
      navigator.platform,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.maxTouchPoints || 0,
    ];

    const fingerprint = components.join('|');
    return generateDataHash(fingerprint);
  } catch (error) {
    logger.error('デバイスフィンガープリント生成エラー:', error);
    return generateDataHash(`fallback-${Date.now()}-${Math.random()}`);
  }
};

/**
 * セキュリティ情報取得（デバッグ用）
 */
export const getJWTSignerInfo = () => {
  if (import.meta.env.PROD) {
    return { error: 'Production環境では利用できません' };
  }

  return {
    supportedAlgorithms: ['RS256', 'ES256'],
    defaultAlgorithm: 'RS256',
    tokenTypes: ['access', 'refresh'],
    deviceFingerprintEnabled: true,
    mockImplementation: (import.meta.env.MODE as string) !== 'production',
    securityFeatures: [
      'JWT ID (JTI) for revocation',
      'Device fingerprinting',
      'Session management',
      'Multi-tenant support',
      'Expiry validation',
      'Signature verification',
    ],
    timestamp: new Date().toISOString(),
  };
};

// JWT Signerオブジェクト（クラス風インターフェース）
export const RipiaJWTSigner = {
  generateTokenPair,
  verifyToken,
  extractPayload: extractJWTPayload,
  checkExpiry: checkTokenExpiry,
  generateFingerprint: generateDeviceFingerprint,
  getInfo: getJWTSignerInfo,
  signToken: async (
    payload: RipiaJWTPayload,
    algorithm: JWTAlgorithm = 'RS256'
  ): Promise<string> => {
    const config = getJWTConfig(algorithm);
    return signJWT(payload, config);
  },
};

// JWTAlgorithmをre-export
export type { JWTAlgorithm };

// 開発環境でのテスト用エクスポート
if (typeof window !== 'undefined' && import.meta.env.MODE === 'development') {
  (window as typeof window & { RipiaJWTSigner: object }).RipiaJWTSigner =
    RipiaJWTSigner;
}
